import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { FeedbackStepData } from '../../feedback-step.interface';
import { FeedbackQuestion } from '../../../feedback';
import { QUESTION_SCALE_LABELS } from '../../feedback-question.enum';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-stakeholder-feedback-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzRadioModule,
    NzButtonModule,
    NzFormModule,
    NzDividerModule,
  ],
  templateUrl: './stakeholder-feedback-step.component.html',
  styleUrls: ['../../feedback-form.component.scss'],
})
export class StakeholderFeedbackStepComponent implements OnChanges, OnDestroy {
  @Input() stepData!: FeedbackStepData;
  @Input() formGroup!: FormGroup;
  @Input() onNext!: () => void;
  @Input() onPrevious!: () => void;
  @Input() overAllScore = 0;

  private destroy$ = new Subject<void>();

  ngOnChanges() {
    this.setupValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupValidation() {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;

    this.stepData.questions.forEach((question: FeedbackQuestion) => {
      const index = this.getQuestionIndex(question.id);
      if (index >= 0) {
        const control = questionResponses.at(index);
        const ratingScaleControl = control.get('ratingScale');

        if (ratingScaleControl) {
          ratingScaleControl.setValidators([Validators.required]);
          ratingScaleControl.updateValueAndValidity();
        }

        ratingScaleControl?.markAllAsTouched();
      }
    });
  }

  getQuestionIndex(questionId: number): number {
    const questionResponses = this.formGroup.get(
      'questionResponses'
    ) as FormArray;
    return questionResponses.controls.findIndex(
      control => control.get('caseFeedbackQuestionId')?.value === questionId
    );
  }

  getQuestionValidationStatus(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const ratingScale = control.get('ratingScale');

      if (
        ratingScale?.invalid &&
        (ratingScale?.dirty || ratingScale?.touched)
      ) {
        return 'error';
      }
    }
    return '';
  }

  getQuestionErrorTip(questionId: number): string {
    const index = this.getQuestionIndex(questionId);
    if (index >= 0) {
      const questionResponses = this.formGroup.get(
        'questionResponses'
      ) as FormArray;
      const control = questionResponses.at(index);
      const ratingScale = control.get('ratingScale');

      if (
        ratingScale?.invalid &&
        (ratingScale?.dirty || ratingScale?.touched)
      ) {
        if (ratingScale.errors?.['required']) {
          return 'Answer is required';
        }
      }
    }
    return '';
  }

  getProgressLabel(question: FeedbackQuestion, value: number): string {
    const labels = QUESTION_SCALE_LABELS[question.id] || [
      'Very Poor',
      'Poor',
      'Fair',
      'Good',
      'Excellent',
    ];
    return labels[value - 1] || labels[0];
  }
}
